import _ from 'lodash';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IFormFieldRules from '@/models/form-field/IFormFieldRules';
import IOriginalValue from '@/models/original/IOriginalValue';
import IOriginalValuesProperty from '@/models/original/IOriginalValuesProperty';
import HostExchangeService from '@/services/3.6/HostExchangeService';
import { Getters, originalValuesStore, store } from '@/store';
import IOriginalValueAddressed from '@/models/original/IOriginalValueAddressed';
import IOriginalValuesData from '@/models/original/IOriginalValuesData';
import ICompAddresses from '@/models/comp/ICompAddresses';

/**
 * Manager for Original Values data.
 */
export default {
    /**
     * Fetch any original values and put them in the store.
     */
    async fetchAndStore() {
        const hostExchangeService = HostExchangeService.getInstance();
        const originalValuesData = await hostExchangeService.getOriginalValuesFromReport();
        if (originalValuesData) {
            await originalValuesStore.setAllData(originalValuesData);

            const keys = Object.keys(originalValuesData);
            for (const key of keys) {
                const originalValuesProperty = originalValuesData[key];
                const propertyString = JSON.stringify(originalValuesProperty);
                //console.log(`OriginalValuesManager.fetchAndStore(), Property[${key}], Content[${propertyString}]`);
            }
        }
        //console.log(`OriginalValuesManager.fetchAndStore(), Done`);
    },

    /**
     * Get a copy of the original values data. Cloned in order to not be reactive.
     */
    getOriginalValuesDataByAddresses(compAddresses: ICompAddresses): IOriginalValuesData {
        const originalValuesData: IOriginalValuesData = {};

        for (const compAddress of compAddresses) {
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(compAddress);
            const originalValuesProperty = this.getPropertyByAddress(compAddress);
            if (originalValuesProperty) {
                originalValuesData[address1AndUnitNo] = originalValuesProperty;
            }
        }
        return originalValuesData;
    },

    /**
     * Get an original values property by the given comp address.
     * @param address1AndUnitNo
     * @returns the associated original values property or null if it doesn't exist.
     */
    getPropertyByAddress(compAddress: ICompAddress): IOriginalValuesProperty | null {
        const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(compAddress);
        const originalValuesProperty = this.getPropertyByAddress1AndUnitNumber(address1AndUnitNo);
        if (!originalValuesProperty) {
            return null;
        }

        const expandedProperty = _.cloneDeep(originalValuesProperty);
        const comp = store.getters[Getters.CompAddressesV36.CompByAddress](compAddress);
        if (!comp?.sourceData) {
            return expandedProperty;
        }

        const integerFieldIds = new Set(
            expandedProperty
                .filter((value) => Number.isInteger(value.fieldId))
                .map((value) => value.fieldId)
        );
        const existingFieldIds = new Set(expandedProperty.map((value) => value.fieldId));
        const repeatableValuesByFieldId = new Map<number, string>();

        for (const compData of comp.sourceData) {
            if (
                typeof compData.fieldId !== 'number' ||
                Number.isInteger(compData.fieldId) ||
                !integerFieldIds.has(Math.floor(compData.fieldId)) ||
                typeof compData.value !== 'string'
            ) {
                continue;
            }

            const currentValue = repeatableValuesByFieldId.get(compData.fieldId);
            if (currentValue === undefined || (currentValue.trim() === '' && compData.value.trim() !== '')) {
                repeatableValuesByFieldId.set(compData.fieldId, compData.value);
            }
        }

        for (const [fieldId, originalValue] of repeatableValuesByFieldId) {
            if (!existingFieldIds.has(fieldId)) {
                expandedProperty.push({
                    fieldId: fieldId,
                    originalValue: originalValue,
                });
            }
        }

        return expandedProperty;
    },

    /**
     * Get an original values property by the given address1AndUnitNo.
     * @param address1AndUnitNo
     * @returns the associated original values property or null if it doesn't exist.
     */
    getPropertyByAddress1AndUnitNumber(address1AndUnitNo: string): IOriginalValuesProperty | null {
        const originalValuesProperty = originalValuesStore.OriginalValuesByAddress1AndUnitNo(address1AndUnitNo);
        return originalValuesProperty;
    },

    /**
     * Get an original value by the given comp address and field id.
     * @param compAddress
     * @param fieldId
     * @returns the associated original value or null if it doesn't exist.
     */
    getValueByAddressAndFieldId(compAddress: ICompAddress, fieldId: number): IOriginalValue | null {
        const originalValuesProperty = this.getPropertyByAddress(compAddress);
        if (originalValuesProperty) {
            const originalValue = originalValuesProperty.find((ov) => ov.fieldId === fieldId);
            if (originalValue) {
                return originalValue;
            }
        }
        return null;
    },

    /**
     * Update the original values store state using the given submitted field rules and resulting field rules for the
     * comp identified by the given comp address. Will add, replace or delete original values as necessary.
     * @param submittedFieldRules
     * @param resultFieldRules
     * @param compAddress
     */
    async updateFromFieldRules(compAddress: ICompAddress, submittedFieldRules: IFormFieldRules, resultFieldRules: IFormFieldRules) {
        const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(compAddress);

        for (const submittedFieldRule of submittedFieldRules) {
            const originalValue: IOriginalValue = {
                fieldId: submittedFieldRule.fieldId,
                originalValue: submittedFieldRule.value,
            };
            const valueAddressed: IOriginalValueAddressed = {
                address1AndUnitNo: address1AndUnitNo,
                value: originalValue,
            };

            const resultFieldRule = resultFieldRules.find((fr) => fr.fieldId == submittedFieldRule.fieldId);
            if (resultFieldRule) {
                // It's there but we don't check the resultFieldRule.fieldRulesApplied flag
                if (resultFieldRule.value.toLowerCase() !== submittedFieldRule.value.toLowerCase()) {
                    // Resulting value is different
                    const added = await originalValuesStore.addOrReplaceValue(valueAddressed);
                    // console.log(`OriginalValuesManager.updateFromFieldRules(), ${added ? 'Added' : 'Replaced'}, Property[${address1AndUnitNo}], Submitted[${submittedFieldRule.value}] Result[${resultFieldRule.value}] for field[${submittedFieldRule.fieldId}]`);
                } else {
                    // Resulting value is not different so delete any existing
                    const deleted = originalValuesStore.deleteValue(valueAddressed);
                    // if (deleted) {
                    //     console.log(`OriginalValuesManager.updateFromFieldRules(), Deleting, Property[${address1AndUnitNo}], Field[${submittedFieldRule.fieldId}], Submitted[${submittedFieldRule.value}] Result[${resultFieldRule.value}]`);
                    // }
                }
            } else {
                // No resulting field rule was returned so delete any existing
                const deleted = originalValuesStore.deleteValue(valueAddressed);
                // if (deleted) {
                //     console.log(`OriginalValuesManager.updateFromFieldRules(), Deleting, Property[${address1AndUnitNo}], Field[${submittedFieldRule.fieldId}], Submitted[${submittedFieldRule.value}], No result.`);
                // }
            }
        }
    },

    /**
     * Delete an original value by the given address and fieldId
     * @param address1AndUnitNo
     * @param fieldId
     * @returns true on deleted, false when the OriginalValue (by fieldId) or OriginalValuesProperty (by address1AndUnitNo) aren't found in the store
     */
    deleteValueByAddressAndFieldId(address1AndUnitNo: string, fieldId: number): boolean {
        const originalValue: IOriginalValue = {
            fieldId: fieldId,
            originalValue: '',
        };
        const valueAddressed: IOriginalValueAddressed = {
            address1AndUnitNo: address1AndUnitNo,
            value: originalValue,
        };

        const deleted = originalValuesStore.deleteValue(valueAddressed);
        //console.log(`OriginalValuesManager.deleteValueByAddressAndFieldId(), ${deleted ? 'Deleted' : 'Not deleted'}, Property[${address1AndUnitNo}], Field[${fieldId}].`);

        return deleted;
    },
};
